import Upload from '@crehana/compass.icons.outline/Upload';
import { Radio } from '@crehana/compass.ui';
import { Input } from '@crehana/compass.ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crehana/compass.ui/select';
import { cn } from '@crehana/compass.ui/utils';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@crehana/forms/components';
import { useFormContext } from '@crehana/forms/rhf';
import type { AtsJobVacancy } from '@/generated/graphql/types';
import {
  AtsDisabilityCandidatesEnum,
  AtsGenderCandidatesEnum,
  AtsOriginCandidatesEnum,
} from '@/generated/graphql/types';
import { AtsCandidateFormVisibilityTypeEnum } from '@/generated/graphql/types.mock';
import { DOCUMENTS } from '@/shared/components/input-file/utils';
import { useCountryCodesWithDefault } from '@/shared/hooks/use-default-country-code';
import { useTranslationSharedCandidateForm } from '../i18n/use-translations';
import type { TCandidateForm } from '../types';
import {
  ensureHttps,
  getFormLabelClassName,
  parsePhoneValue,
  processLinkedInOnBlur,
  processLinkedInOnChange,
  updatePhoneValue,
} from '../utils';
import { UploadCandidateFormAttachments } from './upload-candidate-form-attachments';

type TGeneralInformationProps = {
  type: 'internal' | 'external';
  jobVacancyPublicInfo: AtsJobVacancy;
  organizationId: number;
  preview?: boolean;
  disabledPrincipalInformation?: boolean;
};

// Candidate Source Options
const CANDIDATE_SOURCE_OPTIONS = [
  {
    value: AtsOriginCandidatesEnum.Linkedin,
    translationKey: 'CANDIDATE_SOURCE_LINKEDIN',
  },
  {
    value: AtsOriginCandidatesEnum.CompanyPortal,
    translationKey: 'CANDIDATE_SOURCE_COMPANY_WEBSITE',
  },
  {
    value: AtsOriginCandidatesEnum.Referred,
    translationKey: 'CANDIDATE_SOURCE_REFERRED',
  },
  {
    value: AtsOriginCandidatesEnum.SocialNetwork,
    translationKey: 'CANDIDATE_SOURCE_SOCIAL_MEDIA',
  },
  {
    value: AtsOriginCandidatesEnum.UploadMassive,
    translationKey: 'CANDIDATE_SOURCE_UPLOAD_MASSIVE',
  },
  {
    value: AtsOriginCandidatesEnum.Unspecified,
    translationKey: 'CANDIDATE_SOURCE_OTHER',
  },
] as const;

// Gender Options
const GENDER_OPTIONS = [
  {
    value: AtsGenderCandidatesEnum.Female,
    translationKey: 'GENDER_FEMALE',
  },
  {
    value: AtsGenderCandidatesEnum.Male,
    translationKey: 'GENDER_MALE',
  },
  {
    value: AtsGenderCandidatesEnum.Other,
    translationKey: 'GENDER_OTHER',
  },
] as const;

// Disability Options (Yes/No)
const DISABILITY_OPTIONS = [
  {
    value: AtsDisabilityCandidatesEnum.ResponseDisabilityYes,
    translationKey: 'YES',
  },
  {
    value: AtsDisabilityCandidatesEnum.ResponseDisabilityNo,
    translationKey: 'NO',
  },
] as const;

export const GeneralInformation = ({
  type,
  jobVacancyPublicInfo,
  organizationId,
  preview,
  disabledPrincipalInformation = false,
}: TGeneralInformationProps) => {
  const { t } = useTranslationSharedCandidateForm();
  const { control } = useFormContext<TCandidateForm>();
  const { countryCodes, defaultCountryCode } = useCountryCodesWithDefault();

  //Get the personal info map
  const personalInfoMap = Object.fromEntries(
    jobVacancyPublicInfo?.personal_info?.map(item => [
      item.label,
      item.visibility,
    ]) || [],
  );

  return (
    <div>
      {/* Name */}
      <div className="tw-flex tw-w-full tw-mt-3.2">
        <FormField
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="tw-w-full">
              <FormLabel className={getFormLabelClassName(type)}>
                {t('NAMES')}
                <span className="tw-text-alert-500">{t('REQUIRED_ICON')}</span>
              </FormLabel>
              <FormControl className="tw-w-full">
                <Input
                  className={cn(
                    'tw-flex-1  tw-bg-neutral-light-100',
                    error && 'tw-border-alert-400',
                  )}
                  disabled={disabledPrincipalInformation}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {/* Last name */}
      <div className="tw-flex tw-w-full tw-mt-3.2">
        <FormField
          control={control}
          name="last_name"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="tw-w-full">
              <FormLabel className={getFormLabelClassName(type)}>
                {t('LAST_NAME')}
                <span className="tw-text-alert-500">{t('REQUIRED_ICON')}</span>
              </FormLabel>
              <FormControl className="tw-w-full">
                <Input
                  className={cn(
                    'tw-flex-1  tw-bg-neutral-light-100',
                    error && 'tw-border-alert-400',
                  )}
                  disabled={disabledPrincipalInformation}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {/* Email */}
      <div className="tw-flex tw-w-full tw-mt-3.2">
        <FormField
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <FormItem className="tw-w-full">
              <FormLabel className={getFormLabelClassName(type)}>
                {t('EMAIL')}
                <span className="tw-text-alert-500">{t('REQUIRED_ICON')}</span>
              </FormLabel>
              <FormControl className="tw-w-full">
                <Input
                  className={cn(
                    'tw-flex-1  tw-bg-neutral-light-100',
                    error && 'tw-border-alert-400',
                  )}
                  disabled={disabledPrincipalInformation}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {/* Phone */}
      {personalInfoMap['PERSONAL_INFO_PHONE'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="number_phone"
            render={({ field, fieldState: { error } }) => {
              const { countryCode, phoneNumber } = parsePhoneValue(
                field.value,
                countryCodes,
              );
              const selectedCountryCode = countryCode || defaultCountryCode;

              return (
                <FormItem className="tw-w-full">
                  <FormLabel className={getFormLabelClassName(type)}>
                    {t('NUMBER_PHONE')}
                    {personalInfoMap['PERSONAL_INFO_PHONE'] ===
                      AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                      <span className="tw-text-alert-500">
                        {t('REQUIRED_ICON')}
                      </span>
                    )}
                    <p className="tw-font-body5 tw-font-normal tw-text-neutral-light-700 tw-my-0.8">
                      {t('DESCRIPTION_PHONE')}
                    </p>
                  </FormLabel>

                  <FormControl className="tw-w-full">
                    <div
                      className={cn(
                        'tw-flex   tw-w-full tw-items-center tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-overflow-hidden',
                        error && 'tw-border-alert-400',
                      )}
                    >
                      <Select
                        value={countryCode || defaultCountryCode}
                        onValueChange={code => {
                          field.onChange(updatePhoneValue(code, phoneNumber));
                        }}
                      >
                        <SelectTrigger className="tw-w-[12rem] tw-py-0.8 tw-flex-shrink-0 tw-h-full tw-border-0 tw-rounded-0 tw-bg-transparent tw-shadow-none">
                          <SelectValue>{selectedCountryCode}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map(country => (
                            <SelectItem
                              key={country.iso ?? country.value}
                              value={country.value}
                            >
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="tw-flex-1 tw-min-w-0 tw-border-l tw-border-solid tw-border-neutral-light-500">
                        <Input
                          className={cn(
                            'tw-w-full tw-h-full tw-py-0.8 tw-border-0 tw-rounded-0 tw-bg-transparent tw-shadow-none focus-visible:tw-ring-0',
                          )}
                          placeholder={t('DESCRIPTION_PHONE')}
                          value={phoneNumber}
                          onChange={e => {
                            const newValue = e.target.value;
                            // Solo permitir números
                            const regex = /^[0-9]*$/;
                            if (!regex.test(newValue)) {
                              return;
                            }
                            field.onChange(
                              updatePhoneValue(selectedCountryCode, newValue),
                            );
                          }}
                        />
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      )}
      {/* Country */}
      {personalInfoMap['PERSONAL_INFO_COUNTRY'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="country"
            render={({ field, fieldState: { error } }) => (
              <FormItem className="tw-w-full">
                <FormLabel className={getFormLabelClassName(type)}>
                  {t('COUNTRY')}
                  {personalInfoMap['PERSONAL_INFO_COUNTRY'] ===
                    AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                    <span className="tw-text-alert-500">
                      {t('REQUIRED_ICON')}
                    </span>
                  )}
                </FormLabel>
                <FormControl className="tw-w-full">
                  <Input
                    className={cn(
                      'tw-flex-1  tw-bg-neutral-light-100',
                      error && 'tw-border-alert-400',
                    )}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      {/* Resume */}
      {personalInfoMap['PERSONAL_INFO_RESUME'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="resume"
            render={({ field }) => (
              <FormItem className="tw-w-full">
                <FormLabel className={getFormLabelClassName(type)}>
                  {t('CURRICULUM')}
                  {(personalInfoMap['PERSONAL_INFO_RESUME'] ===
                    AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility ||
                    personalInfoMap['PERSONAL_INFO_RESUME'] ===
                      AtsCandidateFormVisibilityTypeEnum.AlwaysRequiredCandidateFormVisibility) && (
                    <span className="tw-text-alert-500">
                      {t('REQUIRED_ICON')}
                    </span>
                  )}
                  <p className="tw-font-body5 tw-font-normal tw-text-neutral-light-700 tw-my-0.8">
                    {t('CURRICULUM_DESCRIPTION')}
                  </p>
                </FormLabel>
                <FormControl>
                  <div className="tw-flex tw-w-full">
                    {preview ? (
                      <div className="tw-btn-primary-xs tw-btn-icon tw-btn-disabled">
                        <Upload
                          size={16}
                          className="tw-text-current group-hover:tw-text-primary-light-500"
                        />
                        {t('UPLOAD_ATTACHMENT')}
                      </div>
                    ) : (
                      <UploadCandidateFormAttachments
                        onUpdateAttachment={value => {
                          field.onChange(value?.toString());
                        }}
                        originalId={organizationId || 0}
                        acceptFileTypes={DOCUMENTS['application/pdf']}
                      />
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      {/* Linkedin */}
      {personalInfoMap['PERSONAL_INFO_LINKEDIN'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="linkedin_url"
            render={({ field, fieldState: { error } }) => {
              const savedValue = field.value || '';
              const displayValue = savedValue ? ensureHttps(savedValue) : '';

              return (
                <FormItem className="tw-w-full">
                  <FormLabel className={getFormLabelClassName(type)}>
                    {t('LINKEDIN')}
                    {personalInfoMap['PERSONAL_INFO_LINKEDIN'] ===
                      AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                      <span className="tw-text-alert-500">
                        {t('REQUIRED_ICON')}
                      </span>
                    )}
                  </FormLabel>
                  <FormControl className="tw-w-full">
                    <Input
                      placeholder="https://"
                      className={cn(
                        'tw-flex-1 tw-bg-neutral-light-100',
                        error && 'tw-border-alert-400',
                      )}
                      value={displayValue}
                      onChange={e => {
                        field.onChange(processLinkedInOnChange(e.target.value));
                      }}
                      onBlur={e => {
                        field.onChange(processLinkedInOnBlur(e.target.value));
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      )}
      {/* Candidate Source */}
      {personalInfoMap['PERSONAL_INFO_CANDIDATE_SOURCE'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="candidate_source"
            render={({ field }) => (
              <FormItem className="tw-w-full">
                <FormLabel className="tw-font-subtitle4">
                  {t('CANDIDATE_SOURCE_TITLE')}
                  {personalInfoMap['PERSONAL_INFO_CANDIDATE_SOURCE'] ===
                    AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                    <span className="tw-text-alert-500">
                      {t('REQUIRED_ICON')}
                    </span>
                  )}
                </FormLabel>
                <FormControl className="tw-w-full">
                  <div className="tw-flex tw-flex-col tw-gap-1.2 tw-pt-1.2">
                    {CANDIDATE_SOURCE_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className="tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-flex tw-items-center tw-p-1.2 tw-cursor-pointer"
                        onClick={() =>
                          field.onChange(
                            field.value === option.value
                              ? undefined
                              : option.value,
                          )
                        }
                      >
                        <div className="tw-pointer-events-none">
                          <Radio
                            checked={field.value === option.value}
                            size={'s'}
                          />
                        </div>
                        <span className="tw-font-body4 tw-ml-1.2">
                          {t(option.translationKey)}
                        </span>
                      </button>
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      {/* Disability */}
      {personalInfoMap['PERSONAL_INFO_DISABILITY'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="disability"
            render={({ field }) => (
              <FormItem className="tw-w-full">
                <FormLabel className="tw-font-subtitle4">
                  {t('DISABILITY_TITLE')}
                  {personalInfoMap['PERSONAL_INFO_DISABILITY'] ===
                    AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                    <span className="tw-text-alert-500">
                      {t('REQUIRED_ICON')}
                    </span>
                  )}
                  <p className="tw-font-body5 tw-font-normal tw-text-neutral-light-700 tw-my-0.8">
                    {t('DISABILITY_DESCRIPTION')}
                  </p>
                </FormLabel>
                <FormControl className="tw-w-full">
                  <div className="tw-flex tw-pt-1.2">
                    {DISABILITY_OPTIONS.map((option, index) => (
                      <button
                        key={option.value.toString()}
                        type="button"
                        className={cn(
                          'tw-w-[13.2rem] tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-flex tw-items-center tw-p-1.2',
                          index === 0 && 'tw-mr-1.2',
                        )}
                        onClick={() =>
                          field.onChange(
                            field.value === option.value
                              ? undefined
                              : option.value,
                          )
                        }
                      >
                        <div className="tw-pointer-events-none">
                          <Radio
                            checked={field.value === option.value}
                            size={'s'}
                          />
                        </div>
                        <span className="tw-font-body4 tw-ml-1.2">
                          {t(option.translationKey)}
                        </span>
                      </button>
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      {/* Gender */}
      {personalInfoMap['PERSONAL_INFO_DIVERSITY_GENDER'] !==
        AtsCandidateFormVisibilityTypeEnum.HiddenCandidateFormVisibility && (
        <div className="tw-flex tw-w-full tw-mt-3.2">
          <FormField
            control={control}
            name="gender"
            render={({ field }) => (
              <FormItem className="tw-w-full">
                <FormLabel className="tw-font-subtitle4">
                  {t('GENDER_TITLE')}
                  {personalInfoMap['PERSONAL_INFO_DIVERSITY_GENDER'] ===
                    AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility && (
                    <span className="tw-text-alert-500">
                      {t('REQUIRED_ICON')}
                    </span>
                  )}
                  <p className="tw-font-body5 tw-font-normal tw-text-neutral-light-700 tw-my-0.8">
                    {t('GENDER_DESCRIPTION')}
                  </p>
                </FormLabel>
                <FormControl className="tw-w-full">
                  <div className="tw-flex tw-flex-col tw-gap-1.2 tw-pt-1.2">
                    {GENDER_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className="tw-bg-neutral-light-100 tw-border tw-border-solid tw-border-neutral-light-500 tw-rounded-0.8 tw-flex tw-items-center tw-p-1.2 tw-cursor-pointer"
                        onClick={() =>
                          field.onChange(
                            field.value === option.value
                              ? undefined
                              : option.value,
                          )
                        }
                      >
                        <div className="tw-pointer-events-none">
                          <Radio
                            checked={field.value === option.value}
                            size={'s'}
                          />
                        </div>
                        <span className="tw-font-body4 tw-ml-1.2">
                          {t(option.translationKey)}
                        </span>
                      </button>
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};
